import { useState } from 'react';

import Button from 'components/buttons/Button';
import { GlassBox } from 'components/glassBox/GlassBox';

import { useCodeVerification } from '../hooks/useCodeVerification';

type CodeVerificationInputProps = {
  eventId: string;
  onSuccess?: () => void;
};

export const CodeVerificationInput = ({ eventId, onSuccess }: CodeVerificationInputProps) => {
  const [code, setCode] = useState('');
  const [verificationError, setVerificationError] = useState<string | null>(null);

  const { mutate: verifyCode, isPending: isVerifying } = useCodeVerification({
    eventId,
    onSuccess: () => {
      setVerificationError(null);
      setCode('');
      onSuccess?.();
    },
    onError: (message) => {
      setVerificationError(message);
    },
  });

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value);
    if (verificationError) {
      setVerificationError(null);
    }
  };

  const handleVerifyCode = () => {
    if (!code.trim()) {
      setVerificationError('Please enter a code');
      return;
    }
    setVerificationError(null);
    verifyCode({ code: code.trim() });
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && code.trim() && !isVerifying) {
      handleVerifyCode();
    }
  };

  return (
    <div className="tw-flex tw-w-full tw-flex-col tw-gap-[20px] lg:tw-w-[550px]">
      <h3 className="tw-font-jost tw-text-[20px] tw-font-bold tw-font-bold tw-leading-[24px] tw-text-white">
        Claim Reward By Entering The Secret Code
      </h3>
      <GlassBox
        gradient="linear-gradient(to bottom right, #2D5EFF 0%, rgba(45, 94, 255, 0.2) 100%)"
        borderWidth="1px"
        className="tw-flex tw-w-full tw-flex-row tw-items-center tw-justify-center tw-gap-[20px] tw-px-[20px] tw-py-[15px] lg:tw-max-w-[550px]"
      >
        <div className="tw-mr-[10px] tw-flex tw-flex-1 tw-flex-row tw-items-center">
          {!code && <div className="tw-h-[20px] tw-w-[1px] tw-bg-[#314272]" />}
          <input
            type="text"
            value={code}
            onChange={handleCodeChange}
            onKeyPress={handleKeyPress}
            disabled={isVerifying}
            className="tw-h-[40px] tw-w-full tw-border-none tw-bg-transparent tw-p-[10px] tw-text-[15px] tw-text-white tw-placeholder-[#314272] tw-outline-none disabled:tw-opacity-50"
            placeholder="Enter the code"
          />
        </div>
        <Button
          text={isVerifying ? 'Verifying...' : 'Enter'}
          size="medium-small"
          color="referral-cta"
          disabled={isVerifying || !code.trim()}
          className="tw-w-[120px] tw-min-w-[120px] lg:tw-w-[120px] lg:tw-min-w-[100px]"
          contentClassName="tw-text-[15px] lg:tw-text-[18px] tw-normal-case tw-font-semibold"
          onClick={handleVerifyCode}
        />
      </GlassBox>

      {verificationError && (
        <span className="tw-text-start tw-font-jost tw-text-[15px] tw-leading-[24px] tw-text-[#FF005E]">
          *{verificationError}
        </span>
      )}
    </div>
  );
};

